/**
 * Runtime configuration.
 *
 * Config is FETCHED at runtime rather than baked in at build time, which is
 * what lets us build one artifact and deploy the identical bundle to test and
 * production, swapping only public/config.json (ARCHITECTURE.md).  What was
 * tested is bit-for-bit what ships.
 *
 * Ported from the sandbox prototype (sandbox/skeleton-pwa/src/config.ts), which
 * proved the pattern.
 */

/** The identity providers this service knows how to speak to. */
export type OidcProvider = 'google' | 'apple';

/**
 * What to CALL each provider, in one place.
 *
 * Shared because two things that must agree word-for-word are written in
 * different runtimes: the sign-in button in the browser, and the email the API
 * sends somebody who asked to reset a password on an account that has none --
 * which tells them to press a button by name. If those two drift, the message
 * directs a stranded person to a control that does not exist.
 */
export const PROVIDER_NAMES: Record<OidcProvider, string> = {
  google: 'Google',
  apple: 'Apple',
};

/** The exact wording on the button, so the email can quote it. */
export const providerButtonLabel = (provider: string): string =>
  `Continue with ${PROVIDER_NAMES[provider as OidcProvider] ?? provider}`;

export interface AppConfig {
  siteName: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  /**
   * Where our own Edge Functions live, as a prefix to put in front of
   * `/api`, `/auth-api` and `/admin-api`.
   *
   * Empty is the normal case and means same-origin: the three deployables and
   * the functions share one origin (ARCHITECTURE.md), so the browser calls
   * `/auth-api/me` with no CORS and no preflight.  Set it to a full origin only
   * if the API is ever hosted somewhere else.
   */
  apiBaseUrl?: string;
  /** Web-push public key.  Empty until notifications are wired up. */
  vapidPublicKey?: string;
  /**
   * Where to tell a stranger to write when they need a person.
   *
   * Read by the public landing page, which is the one screen whose reader has
   * no account, no session and therefore no other way to reach anybody here.
   * Derived by `deploy/web/entrypoint.sh` from the same two variables
   * `supportEmail()` in `supabase/functions/_shared/email.ts` uses, so the
   * address on the page and the address in our mail are the same address.
   *
   * ABSENT rather than guessed when there is nothing to derive it from, and the
   * page then omits the line entirely -- printing `support@undefined` looks like
   * a way to reach somebody and is not.
   */
  supportEmail?: string;
  /**
   * Which identity providers this deployment has actually configured.
   *
   * The buttons are drawn from this rather than hardcoded, because a provider
   * button that is present and cannot work is worse than no button: it is the
   * one control on the sign-in screen a person will try first, and every
   * deployment configures these differently.
   *
   * They SIGN IN; they never register. Signup is disabled in the auth service,
   * so an address with no account is refused -- an invitation is the only way
   * in, by design.
   */
  oidcProviders?: OidcProvider[];
  /**
   * Development only: prefix each notification with the path that drew it --
   * `P- ` for one delivered by a push service, `L- ` for one the running client
   * drew itself.
   *
   * The two are meant to be indistinguishable, which is exactly what makes them
   * hard to tell apart while working on the handover between them.  Written by
   * `./dev/dev.sh config`; absent everywhere else, so production shows neither.
   *
   * It rides the runtime config rather than `import.meta.env.DEV` because the
   * one build that matters here is a PRODUCTION build -- `dev.sh preview` is how
   * push is tested in Firefox, and a build-time flag would be false there.
   */
  devNotificationMarkers?: boolean;
  /**
   * The deployment's STAGE (`test`, `prod`), written by
   * `deploy/web/entrypoint.sh`. Read by the admin site alone, which marks
   * `prod` in its header. Absent locally, and absent means "not production".
   */
  stage?: string;
}

/**
 * The origin to reach Supabase on, which is not always the one in the config.
 *
 * `supabaseUrl` is written by `./dev/dev.sh config` from `supabase status`, so
 * locally it is a LOOPBACK address.  That is correct for the machine running the
 * stack and wrong for every other device: opening the dev site from a phone on
 * the LAN, `127.0.0.1` is the phone, and sign-in, realtime and photos all fail
 * against nothing.
 *
 * So a loopback config is trusted only while the page itself came from
 * loopback.  Served from anywhere else, we assume the proxy in front of us
 * carries the Supabase prefixes too -- which dev/Caddyfile does -- and go
 * same-origin.  An empty value means same-origin unconditionally.
 *
 * Production is untouched: a real project URL is not loopback, so the rule
 * never fires.
 */
const LOOPBACK = /^(127\.|\[?::1\]?$|localhost$)/i;

export function supabaseOrigin(config: AppConfig): string {
  const configured = config.supabaseUrl.replace(/\/$/, '');
  if (typeof window === 'undefined') return configured;
  if (!configured) return window.location.origin;

  try {
    const target = new URL(configured);
    const pageIsLoopback = LOOPBACK.test(window.location.hostname);
    if (LOOPBACK.test(target.hostname) && !pageIsLoopback) return window.location.origin;
  } catch {
    // Not a parseable absolute URL -- treat it as a prefix and leave it alone.
  }
  return configured;
}

let cached: Promise<AppConfig> | null = null;

/**
 * Where this app's config.json lives.
 *
 * It has to be resolved against the app's base path, not the origin root: the
 * three deployables share one origin and are told apart by prefix, so the main
 * site's config is at /app/config.json and the login site's at
 * /login/config.json.
 *
 * `import.meta.env` is Vite's, and this module is also in the Edge Functions'
 * import graph, so it is read defensively -- they never call loadConfig(), but
 * the property access must not throw if they ever do.
 */
function configUrl(): string {
  const base = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
  return `${base}config.json`;
}

export function loadConfig(): Promise<AppConfig> {
  if (!cached) {
    cached = fetch(configUrl(), { cache: 'no-store' }).then((res) => {
      if (!res.ok) {
        throw new Error(
          `Missing ${configUrl()} (HTTP ${res.status}). Copy config.example.json to config.json and fill in this environment's Supabase details.`
        );
      }
      return res.json() as Promise<AppConfig>;
    });
    /* REFUSED rather than defaulted, and here because this is the one place
       every app's config passes through. A deployment that never set
       HARMONY_SITE_NAME otherwise looks exactly like one that did, right up
       until a member reads a placeholder. */
    cached = cached.then((config) => {
      if (!config.siteName?.trim()) {
        throw new Error(
          `${configUrl()} has no "siteName". Set HARMONY_SITE_NAME for this ` +
            'deployment -- deploy/web/entrypoint.sh writes it in, and ' +
            './dev/dev.sh config does locally. There is deliberately no default.'
        );
      }
      setSiteName(config.siteName);
      return config;
    });
  }
  return cached;
}

/** Test seam: drop the memoised config (used by tests and by the admin app's
    environment switcher, once that exists). */
export function resetConfigCache(): void {
  cached = null;
}

/**
 * THE PLATFORM'S OWN NAME, IN ONE PLACE -- AND THERE IS NO DEFAULT.
 *
 * The name is deployment configuration, like the Supabase URL beside it: every
 * screen, email and notification reads it from `HARMONY_SITE_NAME`, which
 * `deploy/web/entrypoint.sh` writes into each app's `config.json` and
 * `deploy/docker-compose.yml` hands to the Edge Functions.
 *
 * NOTHING FALLS BACK TO A STAND-IN. A default is how a placeholder ends up in
 * front of a member: it works everywhere, so nobody notices it was never set,
 * and the one deployment that forgot looks exactly like the ones that did not.
 * Every layer refuses instead -- the build fails, the container exits, the
 * function will not boot, `loadConfig()` throws, `platform_name()` raises --
 * and each says which variable is missing.
 *
 * It is RUNTIME rather than build-time because the whole config seam is: one
 * artifact is built and the identical bundle deployed everywhere, swapping only
 * `config.json` (ARCHITECTURE.md).
 *
 * Deliberately NOT the address. Moving origin costs every installed app, every
 * session and every push subscription -- all per-origin -- while moving the
 * name costs nothing. Separate decisions.
 */

/**
 * The last name we were told, so a cold start does not paint an empty heading.
 *
 * `config.json` is FETCHED, so there is at least one paint before the answer
 * arrives. Seeded from storage synchronously at import, exactly as
 * `applyStoredThemeMode()` is, which means only a first-ever visit to a browser
 * ever sees the empty string -- and on that visit `config.json` is already in
 * flight.
 *
 * This is a CACHE, not a default: it only ever holds a value some deployment
 * actually specified.
 *
 * ONE key for the whole origin, like the theme mode and unlike the message
 * cache: an impersonation tab shows the same service under the same name, so
 * sharing it is right rather than a leak, and there is nothing in it to read.
 */
const SITE_NAME_KEY = 'harmony.siteName';

/* Guarded and wrapped: this module is in the Edge Functions' import graph, and
   Deno's `localStorage` THROWS rather than being absent when there is no
   --location. A private window can throw here too. */
function rememberedSiteName(): string {
  try {
    return globalThis.localStorage?.getItem(SITE_NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

let currentSiteName = rememberedSiteName();
const siteNameListeners = new Set<() => void>();

/**
 * What to call this service, synchronously, wherever you are.
 *
 * Empty only in the window before the first `config.json` of a browser's life
 * has landed. It does NOT throw: a heading that is briefly blank is a far
 * smaller failure than a screen that cannot render, and the refusal that
 * matters has already happened in `loadConfig()`.
 */
export function siteName(): string {
  return currentSiteName;
}

/** Told by `loadConfig()`; exported for tests and for the Style Lab. */
export function setSiteName(name: string | undefined | null): void {
  const next = (name ?? '').trim();
  if (!next || next === currentSiteName) return;
  currentSiteName = next;
  try {
    globalThis.localStorage?.setItem(SITE_NAME_KEY, next);
  } catch {
    // A private window refuses to store. Harmless: it costs one blank frame.
  }
  for (const listen of siteNameListeners) listen();
}

/** Subscribe form, for `useSyncExternalStore` in `@harmony/ui`. */
export function subscribeSiteName(listen: () => void): () => void {
  siteNameListeners.add(listen);
  return () => {
    siteNameListeners.delete(listen);
  };
}

/**
 * Ask for the name without caring about the rest of the config.
 *
 * For an app that would otherwise fetch `config.json` late or not at all -- the
 * admin site reads it only when it needs a photo origin, the login site only on
 * the sign-in screen -- so the name would arrive after the screens that say it
 * had already been drawn.
 *
 * Failure is swallowed HERE and only here: `loadConfig()` has already thrown,
 * and whichever caller actually needs a value out of the config reports it. A
 * red error from a convenience call is noise on top of the real one.
 */
export function primeSiteName(): void {
  void loadConfig().catch(() => {});
}

/**
 * THE NAME AGAIN, AS AN IDENTIFIER -- lower case, ASCII, safe in a URL, an
 * address or a filename.
 *
 * A display name is chosen to be read and an identifier to be parsed, and they
 * must not be one string: `Cana Court` is right on a screen and wrong in a
 * calendar entry's `UID`, where a space is folded, escaped or quietly mangled
 * by whichever client received it.
 *
 * DERIVED from the display name, so renaming stays ONE variable.
 * `HARMONY_SITE_ID` overrides it for the single case that needs the two to
 * differ: a rename that must not disturb identifiers already issued.
 *
 * Pure, and shared by both runtimes, so the identifier the browser would derive
 * and the one the server derives cannot differ. It THROWS on a name with no
 * letters or digits in it rather than inventing one -- `UID:abc@` is a
 * malformed document every calendar client has to guess at.
 */
export function siteIdFrom(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  if (!slug) {
    throw new Error(
      `Cannot derive an identifier from the site name ${JSON.stringify(name)}: ` +
        'it holds no letters or digits. Set HARMONY_SITE_ID explicitly.'
    );
  }
  return slug;
}
